import {CSSProperties, MouseEventHandler, ReactElement, TouchEventHandler, useCallback, useMemo, useState} from 'react';
import './ContextMenuItem.less';
import IconButton from '../IconButton/IconButton';
import Icons, {IconType} from '../IconButton/Icons';
import debounce from 'debounce';
import {IconButtonProps} from "@mui/material";
import type {GenericClickHandler, VoidAnyCallback, VoidCallback} from "../../@types/functions";
import {MOBILE_WIDTH} from "../../lib/picos/breakpoints";

const noop = () => {};

export type ContextMenuItemProps = {
    label: string, // Label is always required
    icon: IconType, // Icon to display
    color?: string, // Color for the icon, optional
    onClick: VoidAnyCallback, // onClick handler - optional because of submenu titles
    subItems?: ContextMenuItemProps[], // If this is a submenu, the submenu items
    afterClick?: VoidCallback, // Called after the onClick handler
    locationInfo?: {
        x: number,
        y: number,
        pageWidth: number,
        pageHeight: number,
        menuWidth: number,
        menuHeight: number
    }, // Information on the parent menu's location
    position?: number, // index of this menu item in the menu
    totalItems?: number, // total number of items in the menu
    showIf?: (arg: any) => boolean,
};

const ContextMenuItem = (props: ContextMenuItemProps) => {
    const [showSubmenu, setShowSubmenu] = useState(false);

    /**
     * Get a set of debounced onClick and onTouch handlers for the passed in function.
     *
     * @param {GenericClickHandler} handler
     */
    const getHandlers = useCallback((handler: GenericClickHandler): {
        onClicked: MouseEventHandler,
        onTouched: TouchEventHandler
    } => {
        const onClick: GenericClickHandler = (e: any) => {
            e.preventDefault();

            handler(e);

            props.afterClick?.();
        };

        const onClicked = debounce<MouseEventHandler>(onClick, 300, {immediate: true});
        const onTouched = debounce(onClick, 300, {immediate: false});

        return {onClicked, onTouched};
    }, [props]);

    /**
     * Generate an IconButton from props
     *
     * @param {Function} onClick
     * @param {IconType} icon
     * @param {string} color
     * @returns {IconButton}
     */
    const getIconButton = useCallback((onClick: MouseEventHandler, icon: IconType, color: string): ReactElement<IconButtonProps> | null => {
        return props.icon ? (
            <IconButton onClick={onClick} type={icon} color={color}/>
        ) : null;
    }, [props.icon]);

    const onMouseOver = () => setShowSubmenu(true);
    const onMouseOut = () => setShowSubmenu(false);

    /**
     * Render a single, normal menu item
     */
    const renderItem = useMemo(() => {
        const {onClicked, onTouched} = getHandlers(props.onClick);

        return (
            <div key={props.label}
                 className="ContextMenuItem">{getIconButton(props.onClick, props.icon, props.color)}
                <div className="ContextMenuItem-Text"
                     onClick={onClicked}
                     onTouchStart={onTouched}
                     onMouseDown={onClicked}
                >{props.label}</div>
            </div>
        )
    }, [getHandlers, getIconButton, props.color, props.icon, props.label, props.onClick]);

    /**
     * Renders a menu item within a submenu
     *
     * @param {ContextMenuItemProps} item
     */
    const renderSubItem = useCallback((item: ContextMenuItemProps) => {
        const {onClicked, onTouched} = getHandlers(item.onClick);

        return (
            <div key={item.label} className="ContextMenuItem">{getIconButton(item.onClick, item.icon, item.color)}
                <div className="ContextMenuItem-Text"
                     onClick={onClicked}
                     onTouchStart={onTouched}
                     onMouseDown={onClicked}
                >{item.label}</div>
            </div>
        );
    }, [getHandlers, getIconButton]);

    /**
     * Renders a submenu and its items.  This happens if props.subItems has values.
     */
    const renderSubmenu = useMemo(() => {
        const isMobile = props.locationInfo.pageWidth <= MOBILE_WIDTH;
        const {x, y, /*menuHeight, */ menuWidth, pageHeight, pageWidth} = props.locationInfo;

        let style: CSSProperties = {};
        if (isMobile) {
            // Mobile
            if (x > (pageWidth / 2)) style.right = `${props.label.length - 1}em`;
            else style.left = `${props.label.length - 1.5}em`;
        }
        else {
            // Desktop
            const bottomHalf = y / pageHeight > .5;

            if (bottomHalf) {
                style.bottom = 0;
            }

            if (x + menuWidth * 2 > pageWidth) {
                style.right = `${(menuWidth)}px`;
                style.transform = 'translate3D(5px, -5px, 0)';

            }
            else {
                style.left = `${menuWidth}px`;
                style.transform = 'translate3D(-5px, -5px, 0)';
            }
        }

        return (
            <div key={props.label + '_submenu'}
                 className="ContextSubmenu ContextMenuItem"
                 onMouseOver={onMouseOver}
                 onMouseOut={onMouseOut}
            >
                {getIconButton(props.onClick || noop, props.icon, props.color)}
                <div key={props.label}
                     className="ContextSubmenuTitle"
                >
                    <div className="ContextSubmenuTitleName">{props.label}</div>
                    <IconButton type={Icons.DOUBLERIGHT}/>
                    <div key={props.label + '_submenuContext'}
                         className="ContextSubmenuContent"
                         style={style}
                    >
                        {showSubmenu && props.subItems.map(item => renderSubItem(item))}
                    </div>
                </div>

            </div>
        );
    }, [getIconButton, props.color, props.icon, props.label, props.locationInfo, props.onClick, props.subItems, renderSubItem, showSubmenu]);

    // Render as a submenu if subItems is present
    if (props.subItems && props.subItems.length > 0) return renderSubmenu;
    // Otherwise just render as an item
    else return renderItem;
}
export default ContextMenuItem;